import { call, put, select, takeLatest } from "redux-saga/effects";
import { fetchOrgProfileError, fetchOrgProfileRequest, fetchOrgProfileSuccess, updateOrgProfileRequest, updateServiceLocationSuccess, updateServiceLocationError,
  updateServiceLocationRequest, updateSkillsetError, updateSkillsetSuccess, saveSkillset, clearPendingProfileImage, } from "./orgProfileSlice";
import { fetchOrgProfileDataApi, updateOrgProfileDataApi, updateOrgServiceLocationApi, saveSkilsetTag, updateOrgSkillsetApi } from "./orgProfileApi";
import { mapOrgProfileResponse } from "./orgProfileMapper";
import { fetchOrganizationViewRequest, fetchOrganizationViewSuccess, fetchOrganizationViewError } from "../../../common/reducers/sessionContextOidc";
import {SHOW_TOAST_WITH_PROPS} from "../../../common/actionConstants"
import { imageGuid, withImageGuid } from "@/common/util";

const selectPendingProfileImage = (state) => state.adminPanel?.orgProfileDetail?.pendingProfileImage;
 
export function* fetchOrgProfileSaga(action) {
  try {
    const response = yield call(fetchOrgProfileDataApi);

    // Map the API response to UI-friendly format
    const mappedOrgProfileData = mapOrgProfileResponse(response);

    // The read side can lag behind organization.updated, so a refetch may still
    // serve the previous logo. Until it serves the announced one, that one wins.
    const pending = yield select(selectPendingProfileImage);
    if (pending) {
      if (imageGuid(mappedOrgProfileData.ProfileFullImage) === pending) {
        yield put(clearPendingProfileImage());
      } else {
        const url = withImageGuid(mappedOrgProfileData.ProfileFullImage, pending);
        if (url) mappedOrgProfileData.ProfileFullImage = url;
      }
    }

    yield put(fetchOrgProfileSuccess({ orgProfileData: mappedOrgProfileData }));
    yield put(fetchOrganizationViewSuccess(mappedOrgProfileData));
  } catch (error) {
    const message = error.message || "Failed to fetch org profile data";
    yield put(fetchOrgProfileError(message));
    yield put(fetchOrganizationViewError(message));
  }
}

export function* updateOrgProfileSaga({ payload }) {
  try {
    const response = yield call(updateOrgProfileDataApi, {...payload});

    if(response.type){
        yield put({
            type: SHOW_TOAST_WITH_PROPS,
            payload: { 
              message: "Org profile data updated successfully.", 
              type: "success", 
              delayTime: 3000, 
              isCloseIcon: true, 
              autoClose: true 
            }
        });
    } else {
        yield put({
            type: SHOW_TOAST_WITH_PROPS,
            payload: { 
              message: "Failed to update org profile data. Please try again.", 
              type: "error",
              delayTime: 3000, 
              isCloseIcon: true, 
              autoClose: true 
            }
        });
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || "Failed to create party.";
    yield put({
        type: SHOW_TOAST_WITH_PROPS,
        payload: { message: errorMessage, type: "error", delayTime: 3000, isCloseIcon: true, autoClose: true },
    });
  }
}

export function* updateServiceLocationSaga({ payload }) {
  try {
    const response = yield call(updateOrgServiceLocationApi, payload);
    if (response.status === 200) {
      yield put(
        updateServiceLocationSuccess({ orgProfileData: response.data }),
      );
    }
  } catch (error) {
    yield put(
      updateServiceLocationError(
        error.message || "Failed to update service location",
      ),
    );
  }
}

export function* saveSkillsetSaga({ payload }) {
    try {
        const newSkillNames = payload.data.filter((val) => !val.id).map((val) => val.name);
        const existingSkills = payload.data.filter((val) => val.id);

        let tagsToUpdateOrg = { data: existingSkills };

        if (newSkillNames.length > 0) {
            let newSkillNamesPayload = {names:newSkillNames}
            const response = yield call(saveSkilsetTag, newSkillNamesPayload);
            if (!response.length) {
                return;
            }
            tagsToUpdateOrg = {
                data: [...(response ?? []), ...existingSkills]
            };
        }

        const finalTagUpload = {
            data: (tagsToUpdateOrg.data || []).map((item) => ({
                skill_id: item.id,
                skill_name: item.name
            }))
        };

        const res = yield call(updateOrgSkillsetApi, finalTagUpload);
        yield put(updateSkillsetSuccess({ orgProfileData: res.data }));
    } catch (error) {
        yield put(
            updateSkillsetError(error.message || "Failed to update skillset")
        );
    }
}

export default function* organisationProfileSaga() {
  yield takeLatest([fetchOrgProfileRequest.type, fetchOrganizationViewRequest.type,], fetchOrgProfileSaga);
  yield takeLatest(updateOrgProfileRequest.type, updateOrgProfileSaga);
  yield takeLatest(updateServiceLocationRequest.type, updateServiceLocationSaga);
  yield takeLatest(saveSkillset.type, saveSkillsetSaga);
}
